import { z } from "zod";
import { ActorTypeSchema } from "../constants/actor-types.constants.js";
import { SubjectTypeSchema } from "../constants/subject-types.constants.js";
import { ActivityActionTypeSchema } from "../registries/activity-types.registry.js";
import {
  ActivityEventIdSchema,
  EventIdPublicSchema,
  EventParticipantIdPublicSchema,
  UserIdPublicSchema,
} from "./branded-id.schemas.js";

const _activityEventSchema = z.object({
  id: ActivityEventIdSchema,
  eventIdPublic: EventIdPublicSchema.nullable(),
  userIdPublic: UserIdPublicSchema.nullable(),
  actionType: ActivityActionTypeSchema,
  // Public projection of the actor: only the origin classification, never the
  // backend `actorId` (an internal `UserId`). Structurally a subset of the
  // backend `activityActor` union, so a `BackendActivityEvent` parses straight
  // into this — the extra `actorId` is stripped by the schema.
  activityActor: z.object({ actorType: ActorTypeSchema }),
  subjectType: SubjectTypeSchema.nullable(),
  occurredAt: z.coerce.date(),
  createdAt: z.coerce.date(),
});

export const ActivityEventSchema = _activityEventSchema.readonly();
export type ActivityEvent = z.infer<typeof ActivityEventSchema>;

/** The other participant of the request an activity row is about. */
export const ActivityOtherParticipantSchema = z
  .object({
    participantIdPublic: EventParticipantIdPublicSchema,
    name: z.string(),
    title: z.string().nullable(),
  })
  .readonly();
export type ActivityOtherParticipant = z.infer<
  typeof ActivityOtherParticipantSchema
>;

/**
 * Who acted on an activity row, from the participant's side. `team` is a host
 * or admin acting on their behalf; `unknown` is a row whose other participant
 * can no longer be resolved, so it cannot tell them from the team.
 */
export const ActivityActedBySchema = z.enum([
  "participant",
  "otherParticipant",
  "team",
  "unknown",
]);
export type ActivityActedBy = z.infer<typeof ActivityActedBySchema>;

/** The participant's side of the request an activity row is about. */
export const ActivityParticipantRoleSchema = z.enum(["requester", "target"]);
export type ActivityParticipantRole = z.infer<
  typeof ActivityParticipantRoleSchema
>;

/** How often a participant opened their own suggestions, and when last. */
export const SuggestionViewsSummarySchema = z
  .object({
    count: z.number().int(),
    lastViewedAt: _activityEventSchema.shape.occurredAt,
  })
  .readonly();
export type SuggestionViewsSummary = z.infer<
  typeof SuggestionViewsSummarySchema
>;
